import { GiftIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isActiveDate } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { Experience, ExperienceStatus, GiftOffer, ProductObject } from "types";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import { makeGiftOfferItem } from "../../models/CreatePurchaseState";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoResults } from "../Common/NoResults";
import { ProductImage } from "../Common/ProductImage";
import { getWindowSchedule } from "../Coupon/CouponRestrictionForm";
import { Loader } from "../Loader";
import { FormTerms, getTerms } from "../Product/EditGiftOffer";
import { GiftOfferTerm } from "../Product/GiftOfferTile";
import { ExperienceTile } from "./ExperienceList";

export const GiftOfferList = ({ giftOffers }: { giftOffers: GiftOffer[] }) => {
    const filteredGiftOffers = giftOffers.filter((giftOffer: GiftOffer) => {
        const schedule = giftOffer.purchaseRule ? getWindowSchedule(giftOffer.purchaseRule, "currentTime") : null;
        return schedule?.end ? isActiveDate(schedule?.end) : true;
    });

    const sortedGiftOffers = filteredGiftOffers.sort((a, b) => {
        if (a.baseGiftProduct) return -1;

        if (b.baseGiftProduct) return 1;

        return a.name.localeCompare(b.name);
    });

    return (
        <div className="py-10">
            <div className="text-lg font-bold">Gifts</div>

            {sortedGiftOffers.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2" data-testid="gift-offer-list">
                    {sortedGiftOffers.map((item) => {
                        const giftOfferTerms = getTerms(item);
                        if (item.experience) {
                            return (
                                <ExperienceGiftOfferTile
                                    key={item.id}
                                    giftOffer={item}
                                    giftOfferTerms={giftOfferTerms}
                                />
                            );
                        }

                        return <GiftOfferTile key={item.id} giftOffer={item} giftOfferTerms={giftOfferTerms} />;
                    })}
                </div>
            )}
        </div>
    );
};

const GiftOfferTile = ({ giftOffer, giftOfferTerms }: { giftOffer: GiftOffer; giftOfferTerms: FormTerms }) => {
    const { baseGiftProduct } = giftOffer;
    const seller = useSeller();
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const isAddingItem = history.location.state?.isAddingItem === true;
    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);

    const addItem = useCreatePurchaseStore((state) => state.actions.addItem);
    const hasAnyItems = useCreatePurchaseStore((state) => state.purchase.items.length > 0);
    const selectedOrganizer = useCreatePurchaseStore((state) => state.organizer);
    const setSeller = useCreatePurchaseStore((state) => state.actions.setSeller);

    const handleGiftOfferSelect = () => {
        if (!isAddingItem && (!selectedOrganizer || hasAnyItems)) {
            resetState();
        }

        const giftOfferPayload = {
            giftOffer,
            giftPreference: seller.preferences.gift!,
            giftOfferTerms,
        };
        setSeller(giftOffer.seller.id);
        addItem(makeGiftOfferItem(giftOfferPayload));

        useCreatePurchaseStore.setState({
            /** If user already has any items in the cart, and tries to add a new experience, it would be a MIST checkout. So rather than redirecting him to `CHECKOUT` step, we'll redirect to `SELECT_DATE` */
            step: hasAnyItems ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
            productObject: ProductObject.GIFT_OFFER,
        });

        history.push("/cart");
    };

    const schedule = giftOffer.purchaseRule ? getWindowSchedule(giftOffer.purchaseRule, "currentTime") : null;
    const offerLastDate = schedule?.end;

    return (
        <div
            className="flex cursor-pointer flex-row rounded py-2 hover:bg-secondary-hover"
            data-testid="experience-list-item"
            onClick={handleGiftOfferSelect}
        >
            {baseGiftProduct ? (
                <div className="ml-0.5 mt-0.5 mr-3 flex-shrink-0 rounded-lg border border-gray p-4">
                    <GiftIcon size="large" />
                </div>
            ) : (
                <ProductImage product={giftOffer} />
            )}

            <div className={clsx("flex flex-col space-y-1", baseGiftProduct ? "justify-center" : null)}>
                <span className="text-md font-bold">{giftOffer.name}</span>

                {baseGiftProduct ? null : (
                    <div className="space-x-2">
                        <Badge color="secondary" size="medium">
                            <GiftOfferTerm giftOffer={giftOffer} />
                        </Badge>
                        {offerLastDate && (
                            <Badge size="medium" color="secondary">
                                Expires {formatDate(offerLastDate, DateFormat.DATE_SHORT)}
                            </Badge>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ExperienceGiftOfferTile = ({
    giftOffer,
    giftOfferTerms,
}: {
    giftOffer: GiftOffer;
    giftOfferTerms: FormTerms;
}) => {
    const { data: experience, isLoading } = useResource<Experience>(["experiences", giftOffer.experience?.id]);
    const seller = useSeller();
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const isAddingItem = history.location.state?.isAddingItem === true;
    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);
    const addItem = useCreatePurchaseStore((state) => state.actions.addItem);
    const hasAnyItems = useCreatePurchaseStore((state) => state.purchase.items.length > 0);
    const selectedOrganizer = useCreatePurchaseStore((state) => state.organizer);
    const setSeller = useCreatePurchaseStore((state) => state.actions.setSeller);

    const handleExperienceSelect = () => {
        if (!isAddingItem && (!selectedOrganizer || hasAnyItems)) {
            resetState();
        }

        const giftOfferPayload = {
            giftOffer,
            giftPreference: seller.preferences.gift!,
            experience,
            giftOfferTerms,
        };

        setSeller(giftOffer.seller.id);
        addItem(makeGiftOfferItem(giftOfferPayload));

        useCreatePurchaseStore.setState({
            step: hasAnyItems ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
            productObject: ProductObject.GIFT_OFFER,
        });

        history.push("/cart");
    };

    if (isLoading || !experience) {
        return <Loader />;
    }

    return experience.status === ExperienceStatus.DRAFT ? null : (
        <ExperienceTile experience={experience} onClick={handleExperienceSelect} />
    );
};
